import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowClockwise, CheckCircle, Warning } from 'shared/icons';
import ConnectionsCard from '../components/health/ConnectionsCard';
import FiscalHealthPanel from '../components/health/FiscalHealthPanel';
import { SectionCard, StatusDot, TopCard } from '../components/health/HealthUi';
import StorageCard from '../components/health/StorageCard';
import WorkersTimeline from '../components/health/WorkersTimeline';
import PageHeader from '../components/layout/PageHeader';
import TelegramSettings from '../components/notifications/TelegramSettings';
import Button from '../components/ui/Button';
import { DateTimeText, Id } from '../components/ui/Mono';
import api from '../services/api';
import { FormatDateTimeBr } from '../utils/date';
import { FormatFileSize, FormatInt } from '../utils/format';

/**
 * Health — monitoramento do melidash, no formato do health.html do Campaign
 * Manager (cards de status no topo + seções), adaptado aos NOSSOS recursos:
 * banco MySQL, JobManager (crons/workers), tokens ML/Bling, fonte legada
 * (Postgres do meli_manager), drift de estoque e pendências operacionais.
 * Auto-refresh a cada 30s, como no legado.
 */
const fmtUptime = (seconds) => {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
};
const fmtDateTime = (iso) => FormatDateTimeBr(iso, { blank: '-' });
const timeAgo = (iso) => {
    if (!iso) return '-';
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'agora';
    if (m < 60) return `${m}m atrás`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h atrás`;
    return `${Math.floor(h / 24)}d atrás`;
};
const fmtInterval = (ms) => {
    if (!ms) return '-';
    const m = Math.round(ms / 60000);
    return m >= 60 ? `${Math.round(m / 60)}h` : `${m}min`;
};

// Abas da página. `?tab=notas-fiscais` abre direto na aba (link de alerta, favorito).
const HEALTH_TABS = [
    { id: 'sistema', label: 'Sistema' },
    { id: 'notas-fiscais', label: 'Notas Fiscais' },
];
const HEALTH_TAB_IDS = new Set(HEALTH_TABS.map((tab) => tab.id));

const tabFromSearch = (search) => {
    const requested = new URLSearchParams(search).get('tab');
    return HEALTH_TAB_IDS.has(requested) ? requested : null;
};

const JOB_STATUS = {
    IDLE: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    RUNNING: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

// Contagem que o servidor não conseguiu ler no prazo vem `null`: mostra traço, nunca um zero inventado.
const countOrDash = (value) => (value === null || value === undefined ? '—' : FormatInt(value));

const SystemHealthPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [jobsView, setJobsView] = useState('tabela');
    // Um worker por vez no estado "disparando", para o botao poder desabilitar
    // sozinho sem esperar o proximo refresh do /health (30s) confirmar.
    const [runningJobs, setRunningJobs] = useState({});

    const load = useCallback(async () => {
        try {
            setError(null);
            const res = await api.get('/health/details');
            setData(res?.data ?? res);
            setUpdatedAt(new Date());
        } catch (err) {
            setError(err.message || 'Erro ao carregar health');
        } finally {
            setLoading(false);
        }
    }, []);

    // Dispara um worker AGORA. O lock no banco ja impede execucao dupla do
    // mesmo worker; o estado local aqui e so para o botao responder na hora.
    const runJob = useCallback(
        async (jobName) => {
            setRunningJobs((prev) => ({ ...prev, [jobName]: true }));
            try {
                await api.post(`/health/jobs/${encodeURIComponent(jobName)}/run`);
            } catch (err) {
                console.error('Falha ao disparar worker:', err);
            } finally {
                setRunningJobs((prev) => {
                    const next = { ...prev };
                    delete next[jobName];
                    return next;
                });
                load();
            }
        },
        [load],
    );

    // auto-refresh 30s, como o health.html do legado
    useEffect(() => {
        load();
        const timer = setInterval(load, 30000);
        return () => clearInterval(timer);
    }, [load]);

    const staleJobs = useMemo(() => (data?.jobs || []).filter((j) => j.is_stale).length, [data]);

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    {[...Array(4)].map((_, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={i} className="h-28 animate-pulse rounded-xl border border-mono-200 bg-mono-100 dark:border-mono-800 dark:bg-mono-900" />
                    ))}
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="space-y-6">
                <div className="rounded-xl border border-mono-200 bg-white p-10 text-center dark:border-mono-800 dark:bg-mono-950">
                    <Warning size={40} className="mx-auto mb-3 text-red-500" />
                    <p className="text-mono-500">{error || 'Sem dados'}</p>
                    <Button onClick={load} size="sm" className="mt-4">
                        Tentar novamente
                    </Button>
                </div>
            </div>
        );
    }

    const db = data.database || {};
    const jm = data.job_manager || {};
    const integ = data.integrations || {};
    const stats = data.stats || {};
    const drift = data.drift || {};

    const integrations = [
        {
            key: 'mercadolivre',
            label: 'Mercado Livre (token)',
            ok: integ.mercadolivre?.valid === true,
            lines: [
                integ.mercadolivre?.expires_at ? `expira ${fmtDateTime(integ.mercadolivre.expires_at)}` : 'sem token',
                integ.mercadolivre?.updated_at ? `atualizado ${timeAgo(integ.mercadolivre.updated_at)}` : null,
            ],
        },
        {
            key: 'bling',
            label: 'Bling (token)',
            ok: integ.bling?.valid === true,
            lines: [integ.bling?.expires_at ? `expira ${fmtDateTime(integ.bling.expires_at)}` : 'sem token'],
        },
        {
            key: 'legacy',
            label: 'Fonte Legada (Campaign Manager)',
            ok: integ.legacy_source?.connected === true,
            warn: !integ.legacy_source?.configured,
            lines: [
                integ.legacy_source?.configured
                    ? integ.legacy_source?.connected
                        ? `${integ.legacy_source.campaigns} campanhas · ${integ.legacy_source.latency_ms}ms`
                        : integ.legacy_source?.error || 'sem conexão'
                    : 'não configurada (LEGACY_PG_HOST)',
            ],
        },
        {
            key: 'mp_webhook',
            label: 'Webhook Mercado Pago',
            ok: integ.mp_webhook?.configured === true,
            warn: !integ.mp_webhook?.configured,
            lines: [integ.mp_webhook?.configured ? 'assinatura configurada' : 'sem assinatura (validação off)'],
        },
        {
            key: 'discord',
            label: 'Alertas (Discord)',
            ok: integ.discord_alerts?.configured === true,
            warn: !integ.discord_alerts?.configured,
            lines: [integ.discord_alerts?.configured ? 'webhook configurado' : 'não configurado'],
        },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-end gap-3">
                {updatedAt ? (
                    <span className="text-xs text-mono-500">Atualizado: {updatedAt.toLocaleTimeString('pt-BR')}</span>
                ) : null}
                <Button onClick={load} size="sm">
                    <ArrowClockwise size={16} className="mr-2" /> Atualizar
                </Button>
            </div>

            {/* Cards de status no topo, como no legado */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                <TopCard
                    label="Banco de Dados"
                    sub={db.host ? `${db.host}:${db.port} · ${db.latency_ms}ms` : db.error}
                >
                    <span className="flex items-center gap-2">
                        <StatusDot ok={db.connected} /> {db.connected ? 'Conectado' : 'Desconectado'}
                    </span>
                </TopCard>
                <TopCard
                    label="Jobs / Crons"
                    sub={`${(data.jobs || []).length} workers registrados${staleJobs > 0 ? ` · ${staleJobs} atrasado(s)` : ''}`}
                >
                    <span className="flex items-center gap-2">
                        <StatusDot ok={jm.started && staleJobs === 0} warn={jm.started && staleJobs > 0} />
                        {jm.started ? 'Rodando' : 'Parado'}
                    </span>
                </TopCard>
                <TopCard
                    label="Uptime"
                    sub={`Memória: ${FormatFileSize(data.memory?.heapUsed || 0)} / ${FormatFileSize(data.memory?.heapTotal || 0)} · Node ${data.app?.node || ''}`}
                >
                    <span className="rounded-md bg-mono-100 px-2 py-0.5 font-mono text-base dark:bg-mono-900">
                        {fmtUptime(data.uptime_seconds || 0)}
                    </span>
                </TopCard>
                <TopCard
                    label="Drift de Estoque (24h)"
                    sub={
                        (drift.buckets || [])
                            .map((b) => `${b.source}/${b.kind.replace('stock_total_mismatch', 'estoque')}: ${b.unresolved}`)
                            .join(' · ') || 'sem divergências'
                    }
                >
                    <span className="flex items-center gap-2">
                        <StatusDot ok={(drift.total_unresolved || 0) === 0} warn={(drift.total_unresolved || 0) > 0} />
                        {FormatInt(drift.total_unresolved)} não resolvidas
                    </span>
                </TopCard>
            </div>

            {/* Visão geral: estatísticas + integrações */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <SectionCard title="Estatísticas Gerais">
                    <table className="w-full text-sm">
                        <tbody>
                            {[
                                [
                                    'Última venda registrada',
                                    <DateTimeText key="ultima-venda" value={stats.last_sale_at} relative />,
                                ],
                                ['Vendas (24h)', countOrDash(stats.sales_24h)],
                                ['Payments no banco', countOrDash(stats.total_payments)],
                                ['Orders sem payment (30d)', countOrDash(stats.payments_pending)],
                                ['Envios sem hidratação (30d)', countOrDash(stats.shipping_pending)],
                            ].map(([label, value]) => (
                                <tr key={label} className="border-b border-mono-50 last:border-b-0 dark:border-mono-900/60">
                                    <td className="px-4 py-2 text-mono-500">{label}</td>
                                    <td className="px-4 py-2 text-right font-semibold tabular-nums text-black dark:text-white">
                                        {value}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </SectionCard>

                <SectionCard title="Integrações">
                    <div className="divide-y divide-mono-50 dark:divide-mono-900/60">
                        {integrations.map((it) => (
                            <div key={it.key} className="flex items-center justify-between px-4 py-2.5">
                                <div>
                                    <p className="flex items-center gap-2 text-sm font-medium text-black dark:text-white">
                                        <StatusDot ok={it.ok} warn={it.warn} /> {it.label}
                                    </p>
                                    {it.lines.filter(Boolean).map((l) => (
                                        <p key={l} className="ml-[18px] text-xs text-mono-500">
                                            {l}
                                        </p>
                                    ))}
                                </div>
                                {it.ok ? (
                                    <CheckCircle size={18} className="text-green-500" />
                                ) : (
                                    <Warning size={18} className={it.warn ? 'text-orange-500' : 'text-red-500'} />
                                )}
                            </div>
                        ))}
                    </div>
                </SectionCard>
            </div>

            <ConnectionsCard />

            <StorageCard />

            <TelegramSettings />

            {/* Jobs/crons — tabela ou linha do tempo */}
            <SectionCard
                title="Workers / Crons"
                badge={
                    <div className="flex items-center gap-2">
                        <span className="rounded-full bg-mono-100 px-2 py-0.5 text-xs font-semibold text-mono-600 dark:bg-mono-800 dark:text-mono-300">
                            {(data.jobs || []).length}
                        </span>
                        {/* Duas leituras do mesmo dado: a tabela responde "qual o
                            estado de cada worker"; a linha do tempo responde "o que
                            rodou, o que roda agora e o que vem". */}
                        <div className="flex overflow-hidden rounded-md border border-mono-300 dark:border-mono-700">
                            {[
                                ['tabela', 'Tabela'],
                                ['timeline', 'Linha do tempo'],
                            ].map(([valor, rotulo]) => (
                                <button
                                    key={valor}
                                    type="button"
                                    onClick={() => setJobsView(valor)}
                                    aria-pressed={jobsView === valor}
                                    className={`px-2 py-0.5 text-[10px] font-semibold transition ${
                                        jobsView === valor
                                            ? 'bg-info-500 text-white'
                                            : 'text-mono-500 hover:bg-mono-100 dark:hover:bg-mono-800'
                                    }`}
                                >
                                    {rotulo}
                                </button>
                            ))}
                        </div>
                    </div>
                }
            >
                {jobsView === 'timeline' ? (
                    <WorkersTimeline jobs={data.jobs || []} onRun={runJob} running={runningJobs} />
                ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-xs">
                        <thead>
                            <tr className="border-b border-mono-100 dark:border-mono-900">
                                {['', 'Job', 'Status', 'Última execução', 'Próxima', 'Intervalo', 'Erro'].map((h, i) => (
                                    <th key={h || i} className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-mono-400">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {(data.jobs || []).map((j) => (
                                <tr key={j.job_name} className="border-b border-mono-50 last:border-b-0 hover:bg-mono-50 dark:border-mono-900/60 dark:hover:bg-mono-900/40">
                                    <td className="px-2 py-2">
                                        <button
                                            type="button"
                                            onClick={() => runJob(j.job_name)}
                                            disabled={j.status === 'RUNNING' || runningJobs[j.job_name]}
                                            title={j.status === 'RUNNING' ? 'Já está executando' : `Executar ${j.job_name} agora`}
                                            className="rounded border border-mono-300 px-1.5 text-[11px] leading-5 text-mono-600 transition hover:border-info-500 hover:text-info-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-mono-700 dark:text-mono-300"
                                        >
                                            {runningJobs[j.job_name] ? '…' : '▶'}
                                        </button>
                                    </td>
                                    <td className="px-4 py-2 text-black dark:text-white">
                                        <Id value={j.job_name} />
                                    </td>
                                    <td className="px-4 py-2">
                                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${JOB_STATUS[j.status] || JOB_STATUS.IDLE}`}>
                                            {j.status || 'IDLE'}
                                        </span>
                                        {j.is_stale ? (
                                            <span className="ml-1.5 inline-flex rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                                                atrasado
                                            </span>
                                        ) : null}
                                    </td>
                                    <td className="px-4 py-2 text-mono-500">
                                        <DateTimeText value={j.last_run} relative />
                                    </td>
                                    <td className="px-4 py-2 text-mono-500">
                                        <DateTimeText value={j.next_run_at} />
                                    </td>
                                    <td className="px-4 py-2 tabular-nums text-mono-500">{fmtInterval(j.interval_ms)}</td>
                                    <td className="max-w-[260px] truncate px-4 py-2 text-red-500" title={j.error_log || ''}>
                                        {j.error_log ? String(j.error_log).slice(0, 80) : ''}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                )}
            </SectionCard>
        </div>
    );
};

const Health = () => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => tabFromSearch(location.search) ?? 'sistema');

    // Navegar para /health?tab=... com a página já aberta também troca a aba.
    useEffect(() => {
        const requested = tabFromSearch(location.search);
        if (requested) {
            setActiveTab(requested);
        }
    }, [location.search]);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Health"
                subtitle={
                    activeTab === 'notas-fiscais'
                        ? 'Notas fiscais — baixadas, guardadas no S3, comunicadas à Occto e salvas no Drive'
                        : 'Monitoramento do sistema — banco, crons, integrações e pendências'
                }
            />

            <div className="border-b border-mono-200 dark:border-mono-800">
                <nav className="flex space-x-8 overflow-x-auto">
                    {HEALTH_TABS.map((tab) => {
                        const isActive = tab.id === activeTab;
                        return (
                            <button
                                type="button"
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                aria-current={isActive ? 'true' : undefined}
                                className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                                    isActive
                                        ? 'border-black text-black dark:border-white dark:text-white'
                                        : 'border-transparent text-mono-500 hover:border-mono-300 hover:text-mono-700 dark:hover:text-mono-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Uma aba montada por vez: o auto-refresh da outra não roda escondido. */}
            {activeTab === 'notas-fiscais' ? <FiscalHealthPanel /> : <SystemHealthPanel />}
        </div>
    );
};

export default Health;
